import { Controller } from '@hotwired/stimulus'

// Registered as `filter-desktop` — `components/index.js` derives the identifier from THIS path, so
// the file cannot move without changing every `data-controller` that names it. The directory has
// held no component of its own since the chip rail replaced `Filter::Desktop::Component`
// (2026-09-03); the rail's form is what uses this, and `item/` and `breadcrumbs/` beside it are
// still real components.

// Where the filter column was scrolled to when the visitor last changed a filter.
//
// `sessionStorage` rather than a variable in this module: the position then survives a full reload
// as well as a Turbo visit, it is per-tab and gone when the tab closes, and it can be read and
// cleared from outside — which a module variable cannot, as the test for this found the hard way.
// Every access is wrapped: a browser in private mode throws on the very first call, and a scroll
// position is a convenience, never a requirement.
const SCROLL_KEY = 'jobs-filter-rail-scroll'

function rememberScrollTop (value) {
  try {
    if (value === null) window.sessionStorage.removeItem(SCROLL_KEY)
    else window.sessionStorage.setItem(SCROLL_KEY, String(value))
  } catch (error) {
    // No storage, no memory. The filter itself is unaffected.
  }
}

// Spent on read: one save, one restore. Otherwise leaving the board and coming back would drop the
// visitor at a position from a filter change they made minutes ago.
function recallScrollTop () {
  try {
    const raw = window.sessionStorage.getItem(SCROLL_KEY)
    if (raw === null) return null

    window.sessionStorage.removeItem(SCROLL_KEY)

    const value = Number(raw)
    return Number.isFinite(value) ? value : null
  } catch (error) {
    return null
  }
}

export default class extends Controller {
  static classes = ['loading']

  handleChange (event) {
    event.preventDefault()

    // Before the visit: `requestSubmit` replaces the whole body, and the new column arrives at 0.
    this.rememberScroll()

    this.enableLoading()

    this.excludeEmptySelections()
    this.element.requestSubmit()
  }

  rememberScroll () {
    const scroller = this.#scroller()
    rememberScrollTop(scroller ? scroller.scrollTop : null)
  }

  // Run from `connect()` rather than from a `turbo:load` action: connect is guaranteed to run for
  // the new form, so nothing here depends on whether Stimulus has bound a window action by the time
  // Turbo dispatches an event. Its cost is that a cached visit renders twice — a preview from the
  // snapshot, then the real response — and connect runs for both, so a preview is skipped by its
  // `data-turbo-preview` marker; without that the saved position would be spent on a snapshot about
  // to be thrown away.
  //
  // An earlier version used `turbo:load` and was replaced after a stand measurement that appeared
  // to show the first filter change after a cold load losing the position. That measurement was
  // wrong: the probe clicked a chip scrolled out of view, the browser brought the focused control
  // into view before the change event, and the position saved was therefore 0 — which looks exactly
  // like a restore that never ran. `turbo:load` fires from `visitCompleted`, after an async render
  // that drains microtasks, so it would very likely have worked too. Recorded so the comment above
  // is not read as evidence of a Turbo race that was never demonstrated.
  connect () {
    if (document.documentElement.hasAttribute('data-turbo-preview')) return

    this.restoreScroll()
  }

  restoreScroll () {
    const top = recallScrollTop()
    if (top === null) return

    const scroller = this.#scroller()
    if (!scroller) return

    // One frame later, so the column has a height. Assigning `scrollTop` to an element the browser
    // has not laid out yet clamps it to 0, which is the bug this method exists to fix.
    requestAnimationFrame(() => { scroller.scrollTop = top })
  }

  // Exclude empty selections so that the search url is sent without empty parameters
  excludeEmptySelections () {
    const selectionsElements = [...this.element.querySelectorAll('select')]

    selectionsElements.forEach(selectElement => {
      const { value } = selectElement
      const hasValue = value && value !== '[]'

      if (hasValue) return

      selectElement.setAttribute('name', '')
    })
  }

  enableLoading () {
    document.body.classList.add(this.loadingClass)
  }

  disableLoading () {
    document.body.classList.remove(this.loadingClass)
  }

  // The nearest ancestor that scrolls. Found by walking up rather than by naming
  // `.layout__aside_left`: that class is design-system's and the rule that makes it scroll is ours
  // (`_filter-sidebar-sticky.scss`), so hardcoding it would let a rename on either side break this
  // silently, with nothing failing anywhere.
  #scroller () {
    let node = this.element.parentElement

    while (node && node !== document.body) {
      const overflow = getComputedStyle(node).overflowY
      if (overflow === 'auto' || overflow === 'scroll') return node

      node = node.parentElement
    }

    return null
  }
}
